/**
 * 水库/灌区时间序列模拟数据生成
 *
 * 生成24小时内、15分钟一次的模拟监测数据，包括：
 * - 库水位 (m)
 * - 入库流量 (m³/s)
 * - 出库流量 (m³/s)
 * - 降雨量
 * - 当前蓄水量（亿 m³）
 * - 气温 (°C)
 * - 湿度 (%)
 *
 * 灌区额外生成需水曲线（万 m³）。
 */

/**
 * 生成24小时×15分钟间隔的时间序列数据
 * @param {Object} reservoir 水库对象（来自 reservoirCatalog）
 * @returns {Object} { times, level, inflow, outflow, rainfall, storage, temp, humidity }
 */
export function generateReservoirTimeSeries(reservoir) {
  if (!reservoir) return null

  const snap = reservoir.snapshot || {}
  const levels = reservoir.characteristic_levels || {}
  const capacities = reservoir.capacities || {}

  // 基准值
  const baseLevel = snap.level || levels.normal_level || 100
  const baseInflow = snap.inflow || 100
  const baseOutflow = snap.outflow || 80
  const totalCapacity = capacities.total_capacity || 1
  const deadLevel = levels.dead_level || baseLevel - 10
  const normalLevel = levels.normal_level || baseLevel

  // 生成24小时×4=96个点（15分钟一次）
  const POINTS = 96
  const times = []
  const levelData = []
  const inflowData = []
  const outflowData = []
  const rainfallData = []
  const storageData = []
  const tempData = []
  const humidityData = []

  const now = new Date()
  for (let i = 0; i < POINTS; i++) {
    const t = new Date(now.getTime() - (POINTS - 1 - i) * 15 * 60 * 1000)
    const hh = String(t.getHours()).padStart(2, '0')
    const mm = String(t.getMinutes()).padStart(2, '0')
    times.push(`${hh}:${mm}`)

    // 用正弦波模拟日内波动
    const phase = (i / POINTS) * Math.PI * 2
    const dayProgress = i / (POINTS - 1)

    // 水位：在基准值附近波动 ±0.3m
    const levelWave = Math.sin(phase * 2) * 0.3 + Math.sin(phase) * 0.15
    levelData.push(Math.round((baseLevel + levelWave) * 100) / 100)

    // 入库流量：日内波动，模拟早晚高峰
    const inflowWave = Math.sin(phase) * 0.25 + Math.sin(phase * 3) * 0.1
    inflowData.push(Math.round(baseInflow * (0.8 + inflowWave * 0.4) * 10) / 10)

    // 出库流量：略滞后于入库
    const outflowWave = Math.sin(phase + 0.5) * 0.2 + Math.sin(phase * 3 + 0.3) * 0.08
    outflowData.push(Math.round(baseOutflow * (0.85 + outflowWave * 0.3) * 10) / 10)

    // 降雨量：模拟零星降雨，大部分时间为0，偶尔有小雨
    const rainProb = Math.sin(phase * 1.5) + 0.3
    rainfallData.push(rainProb > 0.6 ? Math.round(Math.random() * 8 * 10) / 10 : 0)

    // 蓄水量：根据水位计算
    const ratio = Math.max(0, Math.min(1, (baseLevel + levelWave - deadLevel) / (normalLevel - deadLevel)))
    storageData.push(Math.round(totalCapacity * ratio * 1000) / 1000)

    // 气温：日内温差模拟（凌晨低、下午高）
    const tempBase = 18
    const tempWave = Math.sin((dayProgress - 0.25) * Math.PI * 2) * 8
    tempData.push(Math.round((tempBase + tempWave + (Math.random() - 0.5) * 1.5) * 10) / 10)

    // 湿度：与降雨和气温反相关
    const humidityBase = 60
    const humidityWave = -Math.sin((dayProgress - 0.25) * Math.PI * 2) * 15
    const rainBonus = rainfallData[i] > 0 ? 10 : 0
    humidityData.push(Math.round(Math.max(30, Math.min(95, humidityBase + humidityWave + rainBonus + (Math.random() - 0.5) * 5))))
  }

  return {
    times,
    level: levelData,
    inflow: inflowData,
    outflow: outflowData,
    rainfall: rainfallData,
    storage: storageData,
    temp: tempData,
    humidity: humidityData,
  }
}

/**
 * 生成灌区需水曲线（24小时×15分钟）
 * @param {String} name 灌区名称
 * @param {Number} dailyDemand 万 m³/日
 * @returns {Object} { times, demand, supply, deficit }
 */
export function generateIrrigationTimeSeries(name, dailyDemand = 15) {
  const POINTS = 96
  const times = []
  const demandData = []
  const supplyData = []
  const deficitData = []

  const now = new Date()
  for (let i = 0; i < POINTS; i++) {
    const t = new Date(now.getTime() - (POINTS - 1 - i) * 15 * 60 * 1000)
    const hh = String(t.getHours()).padStart(2, '0')
    const mm = String(t.getMinutes()).padStart(2, '0')
    times.push(`${hh}:${mm}`)

    const phase = (i / POINTS) * Math.PI * 2
    // 需水：早晚高峰
    const demandWave = Math.max(0, Math.sin(phase)) * 0.6 + 0.4
    const demand = Math.round(dailyDemand * demandWave / 4 * 100) / 100
    demandData.push(demand)

    // 供水：略低于需水（模拟缺水）
    const supply = Math.round(demand * (0.85 + Math.random() * 0.1) * 100) / 100
    supplyData.push(supply)

    // 缺口
    deficitData.push(Math.round((demand - supply) * 100) / 100)
  }

  return { times, demand: demandData, supply: supplyData, deficit: deficitData }
}

export default { generateReservoirTimeSeries, generateIrrigationTimeSeries }
